import { Vue, Component, PropSync } from "vue-property-decorator";

import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import EssentialsPlugin from "@ckeditor/ckeditor5-essentials/src/essentials";
import BoldPlugin from "@ckeditor/ckeditor5-basic-styles/src/bold";
import ItalicPlugin from "@ckeditor/ckeditor5-basic-styles/src/italic";
import ParagraphPlugin from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import LinkPlugin from "@ckeditor/ckeditor5-link/src/link";
import SourceEditing from "@ckeditor/ckeditor5-source-editing/src/sourceediting";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import ListStyle from "@ckeditor/ckeditor5-list/src/liststyle";
import ImageInsert from "@ckeditor/ckeditor5-image/src/imageinsert";
import Table from "@ckeditor/ckeditor5-table/src/table";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import UploadAdapter from "@/components/editor/UploadAdapter";
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import CodeBlock from '@ckeditor/ckeditor5-code-block/src/codeblock';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackground from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';

@Component
export default class Editor extends Vue {
  @PropSync("editorProp", { type: String }) editorContent;
  editor = ClassicEditor;
  editorConfig = {
    plugins: [
      EssentialsPlugin,
      BoldPlugin,
      ItalicPlugin,
      ParagraphPlugin,
      SourceEditing,
      LinkPlugin,
      Heading,
      ListStyle,
      ImageInsert,
      Table,
      TableToolbar,
      Image,
      ImageToolbar,
      ImageCaption,
      ImageStyle,
      ImageResize,
      BlockQuote,
      HorizontalLine,
      CodeBlock,
      Alignment,
      FontSize,
      FontColor,
      FontBackground,
      FontFamily,
    ],
    extraPlugins: [this.uploader],
    fontSize: {
      options: [
        8, 10, 12, 'default', 16, 18, 20, 22, 24, 26, 28, 30, 32
      ]
    },
    fontColor: {
      colors: [
        {
          color: '#CB2D35',
          label: 'Red'
        },
        {
          color: '#3f51b5',
          label: 'Blue'
        },
        {
          color: '#82BC89',
          label: 'Green'
        },
        {
          color: 'hsl(60, 75%, 60%)',
          label: 'Yellow'
        },
        {
          color: '#B1B5B6',
          label: 'Gray'
        },
        {
          color: '#000000',
          label: 'Black'
        },
        {
          color: '#ffffff',
          label: 'White',
          hasBorder: true
        }
      ]
    },
    fontBackgroundColor: {
      colors: [
        {
          color: '#CB2D35',
          label: 'Red'
        },
        {
          color: '#3f51b5',
          label: 'Blue'
        },
        {
          color: '#82BC89',
          label: 'Green'
        },
        {
          color: 'hsl(60, 75%, 60%)',
          label: 'Yellow'
        },
        {
          color: '#B1B5B6',
          label: 'Gray'
        },
        {
          color: '#000000',
          label: 'Black'
        },
        {
          color: '#ffffff',
          label: 'White',
          hasBorder: true
        }
      ]
    },
    fontFamily: {
      options: [
        'default',
        'Arial, sans-serif',
        'Helvetica, sans-serif',
        'Times New Roman, sans-seri',
        'Verdana, sans-seri',
        'Courier New, sans-seri',
        'Tahoma, sans-seri',
        'Georgia, sans-seri',
        'Calibri, sans-seri',
        'Ubuntu Mono, Courier New, Courier, monospace'
      ]
    },
    toolbar: {
      items: [
        "heading", "|",
        "bold",
        "italic",
        "fontFamily",
        "fontSize",
        "fontColor",
        "fontBackgroundColor", "|",
        "alignment", "|",
        "bulletedList",
        "numberedList", "|",
        "blockQuote",
        "horizontalLine",
        "link", "|",
        "undo",
        "redo", "|",
        "insertImage",
        "insertTable",
        "sourceEditing", "|",
        "codeBlock",
      ],
      shouldNotGroupWhenFull: true,
      table: {
        contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"]
      }
    },
    image: {
      toolbar: [
        'imageStyle:alignBlockLeft',
        'imageStyle:alignCenter',
        'imageStyle:alignBlockRight',
        '|',
        'imageStyle:block',
        'imageStyle:inline',
        'imageStyle:side',
        '|',
        'toggleImageCaption',
        'imageTextAlternative',
      ]
    },
    link: {
      addTargetToExternalLinks: true,
      decorators: [
        {
          mode: 'manual',
          label: 'Button Primary',
          attributes: {
            class: 'btn-template btn-blue',
          }
        },
        {
          mode: 'manual',
          label: 'Button Primary (green)',
          attributes: {
            class: 'btn-template btn-green',
          }
        },
        {
          mode: 'manual',
          label: 'Button Primary (red)',
          attributes: {
            class: 'btn-template btn-red',
          }
        },
        {
          mode: 'manual',
          label: 'Button Primary (gray)',
          attributes: {
            class: 'btn-template btn-gray',
          }
        },
        {
          mode: 'manual',
          label: 'Button Primary (black)',
          attributes: {
            class: 'btn-template btn-black',
          }
        },
        {
          mode: 'manual',
          label: 'Button Primary (white)',
          attributes: {
            class: 'btn-template btn-white',
          }
        }
      ]
    }
  };
  uploader(editor) {
    editor.plugins.get( 'FileRepository' ).createUploadAdapter = ( loader ) => {
      return new UploadAdapter( loader );
    };
  }
  onEditorBlur() {
    const btns = document.querySelectorAll('.ck-reset.ck-list .ck-switchbutton');

    let isCheckOff = true;
    for (let i = 0; i < btns.length; i++) {
      if(btns[i].classList.contains('ck-on')) {
        isCheckOff = false;
      }
    }

    if(isCheckOff) {
      btns.forEach((x) => {
        x.setAttribute("style", "");
      });
    } else {
      btns.forEach((x, index) => {
        if(!x.classList.contains('ck-on')) {
          x.setAttribute("style", "pointer-events: none; opacity: 0.7;");
        } else {
          x.setAttribute("style", "");
        }
      });
    }

    for (let i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', () => {
        if(btns[i].classList.contains('ck-on')){
          btns.forEach((x, index) => {
            if(index !== i) {
              x.setAttribute("style", "pointer-events: none; opacity: 0.7;");
            }
          });
        } else {
          btns.forEach((x) => {
            x.setAttribute("style", "");
          });
        }
      });
    }
  }
}